import type { TimeGranularity } from "app/pages/app-intelligence/types";

import { TableWidget } from "../TableWidget";

import { geographyColumns } from "./config";

type GeographyItem = { [K in TimeGranularity]: { Average: number; Confidence: number } };

type GeographyResponse = Record<
    number,
    {
        BounceRate: GeographyItem;
        Duration: GeographyItem;
        PagePerVisit: GeographyItem;
        PagesViews: GeographyItem;
        TrafficShare: GeographyItem;
    }
>;

export class SegmentsGeographyWidget extends TableWidget {
    constructor() {
        super();

        this._viewOptions.hasCountryLegend = true;
    }

    static getWidgetMetadataType() {
        return "SegmentsGeography";
    }

    static getWidgetResourceType() {
        return "Data";
    }

    static getWidgetDashboardType() {
        return "Table";
    }

    public isPptSupported = () => {
        return false;
    };

    public getData() {
        const sid = this._params.segmentId || this._params.keys;
        this._params.sid = sid;

        return super.getData();
    }

    private prepareTableData = (countryData: GeographyItem, granularity: TimeGranularity) => {
        return Object.keys(countryData).reduce((acc, metric) => {
            if (metric !== "TrafficShare") {
                acc[metric] = countryData[metric][granularity]?.Average;
            } else {
                acc[metric] = countryData[metric].Average;
            }

            return acc;
        }, {});
    };

    private transformData(rawResponse: GeographyResponse, granularity: TimeGranularity) {
        const transformedData = { Data: null };

        transformedData.Data = Object.keys(rawResponse)
            .reduce((acc, country) => {
                const tableData = this.prepareTableData(rawResponse[country], granularity);

                acc.push({ ...tableData, Country: country });

                return acc;
            }, [])
            .sort((firstGeographyItem, secondGeographyItem) => {
                if (firstGeographyItem.TrafficShare < secondGeographyItem.TrafficShare) {
                    return 1;
                }
                if (firstGeographyItem.TrafficShare > secondGeographyItem.TrafficShare) {
                    return -1;
                }
                return 0;
            });

        return transformedData;
    }

    public async onNewDataReceived(response: GeographyResponse): Promise<void> {
        const granularity = this._params?.timeGranularity as TimeGranularity;
        const transformedResponse = this.transformData(response, granularity);
        await super.onNewDataReceived(transformedResponse);
    }

    protected getMetricColumnsConfig() {
        return geographyColumns;
    }

    public getProUrl() {
        const properties = this._widgetConfig?.properties;
        const { webSource, country, duration, key, filters } = properties;
        const segmentId = filters.segmentId;

        const state = this._metricConfig.state;

        const result = {
            duration,
            country,
            webSource,
            id: key[0].segmentId || segmentId,
            mode: "single",
        };

        if (typeof state === "undefined") {
            return this._swNavigator.href("home", {}, {});
        }

        return this._swNavigator.href(state, result, {});
    }
}

SegmentsGeographyWidget.register();
